import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import './ProfileEditModal.css';

// Constants for dropdowns
const LANGUAGES = ['Arabic', 'English', 'French', 'Spanish', 'German', 'Italian', 'Portuguese', 'Russian', 'Chinese', 'Japanese', 'Korean', 'Hindi', 'Turkish'];
const LANGUAGE_LEVELS = ['Native', 'Fluent', 'Advanced', 'Intermediate', 'Beginner'];
const SUBJECTS = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'Science',
  'English', 'Arabic', 'French', 'Spanish', 'History', 'Geography',
  'Computer Science', 'Programming', 'Art', 'Music', 'Physical Education', 'Other'
];

// Subject types/specializations for each subject
const SUBJECT_TYPES = {
  'Mathematics': ['Algebra', 'Calculus', 'Geometry', 'Trigonometry', 'Statistics', 'Linear Algebra', 'Number Theory', 'Pre-Calculus'],
  'Physics': ['Mechanics', 'Thermodynamics', 'Optics', 'Electromagnetism', 'Quantum Physics', 'Nuclear Physics', 'Astrophysics'],
  'Chemistry': ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Biochemistry', 'Analytical Chemistry'],
  'Biology': ['Molecular Biology', 'Genetics', 'Ecology', 'Anatomy', 'Physiology', 'Microbiology', 'Botany', 'Zoology'],
  'Science': ['General Science', 'Environmental Science', 'Earth Science'],
  'English': ['Grammar', 'Writing', 'Literature', 'Reading Comprehension', 'Speaking', 'IELTS Preparation', 'TOEFL Preparation', 'Business English'],
  'Arabic': ['Grammar (النحو)', 'Morphology (الصرف)', 'Literature (الأدب)', 'Reading', 'Writing', 'Conversation'],
  'French': ['Grammar', 'Conversation', 'Literature', 'DELF Preparation', 'Business French'],
  'Spanish': ['Grammar', 'Conversation', 'Literature', 'DELE Preparation', 'Business Spanish'],
  'History': ['World History', 'Middle Eastern History', 'Modern History', 'Ancient History', 'Islamic History'],
  'Geography': ['Physical Geography', 'Human Geography', 'Cartography', 'GIS'],
  'Computer Science': ['Data Structures', 'Algorithms', 'Databases', 'Networks', 'Operating Systems', 'Cybersecurity', 'AI/Machine Learning'],
  'Programming': ['Python', 'JavaScript', 'Java', 'C++', 'C#', 'Web Development', 'Mobile Development', 'Game Development'],
  'Art': ['Drawing', 'Painting', 'Sculpture', 'Digital Art', 'Art History'],
  'Music': ['Piano', 'Guitar', 'Violin', 'Voice/Singing', 'Music Theory', 'Oud', 'Drums'],
  'Physical Education': ['Fitness', 'Sports Training', 'Yoga', 'Swimming'],
  'Other': []
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = Array.from({ length: 50 }, (_, i) => (new Date().getFullYear() - i).toString());

const BAHRAIN_BANKS = [
  { value: 'National Bank of Bahrain', labelEn: 'National Bank of Bahrain', labelAr: 'بنك البحرين الوطني' },
  { value: 'Bank of Bahrain and Kuwait', labelEn: 'Bank of Bahrain and Kuwait', labelAr: 'بنك البحرين والكويت' },
  { value: 'Ahli United Bank', labelEn: 'Ahli United Bank', labelAr: 'البنك الأهلي المتحد' },
  { value: 'Al Salam Bank', labelEn: 'Al Salam Bank', labelAr: 'بنك السلام' },
  { value: 'Bahrain Islamic Bank', labelEn: 'Bahrain Islamic Bank', labelAr: 'بنك البحرين الإسلامي' },
  { value: 'Kuwait Finance House', labelEn: 'Kuwait Finance House', labelAr: 'بيت التمويل الكويتي' },
  { value: 'Ithmaar Bank', labelEn: 'Ithmaar Bank', labelAr: 'بنك إثمار' },
  { value: 'ABC Bank', labelEn: 'ABC Bank', labelAr: 'بنك ABC' },
  { value: 'Standard Chartered', labelEn: 'Standard Chartered', labelAr: 'ستاندرد تشارترد' },
  { value: 'HSBC', labelEn: 'HSBC', labelAr: 'HSBC' },
  { value: 'Citibank', labelEn: 'Citibank', labelAr: 'سيتي بنك' },
  { value: 'Other', labelEn: 'Other', labelAr: 'آخر' }
];

const PRICE_OPTIONS = [];
for (let i = 4; i <= 25; i += 0.5) {
  PRICE_OPTIONS.push({ value: i, label: `${i} BD` });
}

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = {
  EN: { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' },
  AR: { sunday: 'الأحد', monday: 'الإثنين', tuesday: 'الثلاثاء', wednesday: 'الأربعاء', thursday: 'الخميس', friday: 'الجمعة', saturday: 'السبت' }
};

// Generate time slots
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const hour12 = hour % 12 || 12;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const time12 = `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
      slots.push({ time24, time12 });
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, placeholder, isArabic, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculate dropdown position when opening
  const handleOpen = () => {
    if (disabled) return;
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    }
    setIsOpen(!isOpen);
  };

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? (selectedOption.label || selectedOption.value) : placeholder;

  return (
    <div ref={dropdownRef} className="edit-modal-dropdown">
      <div
        ref={triggerRef}
        onClick={handleOpen}
        className={`edit-modal-dropdown-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
      >
        <span>{displayText}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {isOpen && (
        <div
          className="edit-modal-dropdown-menu"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: '280px'
          }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`edit-modal-dropdown-item ${value === option.value ? 'selected' : ''}`}
            >
              {option.label || option.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Information Editor
const InformationEditor = ({ data, onChange, isArabic }) => {
  const [localData, setLocalData] = useState({
    full_name: data.full_name || '',
    phone: data.phone || '',
    email: data.email || '',
    date_of_birth_day: data.date_of_birth_day || '',
    date_of_birth_month: data.date_of_birth_month || '',
    date_of_birth_year: data.date_of_birth_year || '',
    gender: data.gender || ''
  });

  const handleChange = (field, value) => {
    const newData = { ...localData, [field]: value };
    setLocalData(newData);
    onChange(newData);
  };

  const days = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  const months = MONTHS.map(m => ({ value: m, label: m }));
  const years = Array.from({ length: 80 }, (_, i) => {
    const year = new Date().getFullYear() - 10 - i;
    return { value: String(year), label: String(year) };
  });

  return (
    <div className="edit-modal-form">
      <div className="edit-modal-field">
        <label>{isArabic ? 'الاسم الكامل' : 'Full Name'}</label>
        <input
          type="text"
          value={localData.full_name}
          onChange={(e) => handleChange('full_name', e.target.value)}
          placeholder={isArabic ? 'أدخل اسمك الكامل' : 'Enter your full name'}
        />
      </div>
      <div className="edit-modal-field">
        <label>{isArabic ? 'رقم الهاتف' : 'Phone Number'}</label>
        <div className="edit-modal-phone-input">
          <span className="phone-prefix">+973</span>
          <input
            type="tel"
            value={localData.phone}
            onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="XXXXXXXX"
          />
        </div>
      </div>
      <div className="edit-modal-field">
        <label>{isArabic ? 'البريد الإلكتروني' : 'Email'}</label>
        <input
          type="email"
          value={localData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
          disabled
        />
      </div>
      <div className="edit-modal-field">
        <label>{isArabic ? 'تاريخ الميلاد' : 'Date of Birth'}</label>
        <div className="edit-modal-dob-row">
          <CustomDropdown
            value={localData.date_of_birth_day}
            onChange={(val) => handleChange('date_of_birth_day', val)}
            options={days}
            placeholder={isArabic ? 'اليوم' : 'Day'}
            isArabic={isArabic}
          />
          <CustomDropdown
            value={localData.date_of_birth_month}
            onChange={(val) => handleChange('date_of_birth_month', val)}
            options={months}
            placeholder={isArabic ? 'الشهر' : 'Month'}
            isArabic={isArabic}
          />
          <CustomDropdown
            value={localData.date_of_birth_year}
            onChange={(val) => handleChange('date_of_birth_year', val)}
            options={years}
            placeholder={isArabic ? 'السنة' : 'Year'}
            isArabic={isArabic}
          />
        </div>
      </div>
      <div className="edit-modal-field">
        <label>{isArabic ? 'الجنس' : 'Gender'}</label>
        <div className="edit-modal-radio-group">
          <label className={`edit-modal-radio ${localData.gender === 'Male' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="gender"
              value="Male"
              checked={localData.gender === 'Male'}
              onChange={() => handleChange('gender', 'Male')}
            />
            <span>{isArabic ? 'ذكر' : 'Male'}</span>
          </label>
          <label className={`edit-modal-radio ${localData.gender === 'Female' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="gender"
              value="Female"
              checked={localData.gender === 'Female'}
              onChange={() => handleChange('gender', 'Female')}
            />
            <span>{isArabic ? 'أنثى' : 'Female'}</span>
          </label>
        </div>
      </div>
    </div>
  );
};

// About Me Editor
const AboutMeEditor = ({ data, onChange, isArabic }) => {
  const [bio, setBio] = useState(data.short_bio || '');

  const handleChange = (value) => {
    setBio(value);
    onChange({ short_bio: value });
  };

  return (
    <div className="edit-modal-form">
      <div className="edit-modal-field">
        <label>{isArabic ? 'نبذة عنك' : 'About You'}</label>
        <textarea
          value={bio}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={isArabic ? 'اكتب نبذة عنك...' : 'Write about yourself...'}
          rows={6}
          maxLength={500}
        />
        <div className="edit-modal-char-count">{bio.length}/500</div>
      </div>
    </div>
  );
};

// Subjects Editor
const SubjectsEditor = ({ data, onChange, isArabic }) => {
  // Support both single subject (legacy) and multiple subjects array
  const initialSubjects = data.subjects && Array.isArray(data.subjects)
    ? data.subjects
    : (data.subject ? [data.subject] : []);
  const [subjects, setSubjects] = useState(initialSubjects);

  // Subject types: { Mathematics: ['Algebra', 'Calculus'], Physics: ['Mechanics'] }
  const [subjectTypes, setSubjectTypes] = useState(data.subject_types || {});

  const MAX_SUBJECTS = 3;

  // Find any legacy/invalid subjects that aren't in the main SUBJECTS list
  const legacySubjects = subjects.filter(s => !SUBJECTS.includes(s));

  const toggleSubject = (subjectName) => {
    let newSubjects;
    let newSubjectTypes = { ...subjectTypes };

    if (subjects.includes(subjectName)) {
      // Remove subject and its types
      newSubjects = subjects.filter(s => s !== subjectName);
      delete newSubjectTypes[subjectName];
    } else {
      // Add subject (only if under limit)
      if (subjects.length >= MAX_SUBJECTS) return;
      newSubjects = [...subjects, subjectName];
      // Initialize with empty types array
      newSubjectTypes[subjectName] = [];
    }

    setSubjects(newSubjects);
    setSubjectTypes(newSubjectTypes);

    // Send both formats for compatibility
    onChange({
      subjects: newSubjects,
      subject: newSubjects[0] || '',
      subject_types: newSubjectTypes
    });
  };

  const toggleSubjectType = (subject, type) => {
    const currentTypes = subjectTypes[subject] || [];
    let newTypes;

    if (currentTypes.includes(type)) {
      newTypes = currentTypes.filter(t => t !== type);
    } else {
      newTypes = [...currentTypes, type];
    }

    const newSubjectTypes = { ...subjectTypes, [subject]: newTypes };
    setSubjectTypes(newSubjectTypes);

    onChange({
      subjects,
      subject: subjects[0] || '',
      subject_types: newSubjectTypes
    });
  };

  return (
    <div className="edit-modal-form">
      {/* Show legacy subjects that need to be removed */}
      {legacySubjects.length > 0 && (
        <div className="edit-modal-field">
          <label style={{ color: '#dc2626' }}>
            {isArabic ? 'مواد قديمة (انقر لإزالتها)' : 'Legacy subjects (click to remove)'}
          </label>
          <div className="edit-modal-chip-grid">
            {legacySubjects.map(subjectName => (
              <button
                key={subjectName}
                type="button"
                className="edit-modal-chip selected"
                onClick={() => toggleSubject(subjectName)}
                style={{
                  background: '#fef2f2',
                  borderColor: '#ef4444',
                  color: '#dc2626'
                }}
              >
                {subjectName} ✕
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="edit-modal-field">
        <label>
          {isArabic ? 'المواد' : 'Subjects'}
          <span style={{ fontWeight: 'normal', fontSize: '13px', color: '#6b7280', marginLeft: '8px' }}>
            ({subjects.length}/{MAX_SUBJECTS} {isArabic ? 'مواد كحد أقصى' : 'max'})
          </span>
        </label>
        <div className="edit-modal-chip-grid">
          {SUBJECTS.map(subjectName => (
            <button
              key={subjectName}
              type="button"
              className={`edit-modal-chip ${subjects.includes(subjectName) ? 'selected' : ''}`}
              onClick={() => toggleSubject(subjectName)}
              disabled={!subjects.includes(subjectName) && subjects.length >= MAX_SUBJECTS}
              style={{
                opacity: (!subjects.includes(subjectName) && subjects.length >= MAX_SUBJECTS) ? 0.5 : 1,
                cursor: (!subjects.includes(subjectName) && subjects.length >= MAX_SUBJECTS) ? 'not-allowed' : 'pointer'
              }}
            >
              {subjectName}
            </button>
          ))}
        </div>
      </div>

      {/* Subject Types/Specializations for each selected subject */}
      {subjects.filter(s => SUBJECTS.includes(s) && SUBJECT_TYPES[s]?.length > 0).map(subject => (
        <div key={subject} className="edit-modal-field" style={{
          background: '#f9fafb',
          padding: '16px',
          borderRadius: '10px',
          border: '1px solid #e5e7eb'
        }}>
          <label style={{ marginBottom: '12px', display: 'block' }}>
            {isArabic ? `تخصصات ${subject}` : `${subject} Specializations`}
            <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
              {isArabic ? 'اختر التخصصات التي تدرسها' : 'Select the topics you teach'}
            </span>
          </label>
          <div className="edit-modal-chip-grid">
            {SUBJECT_TYPES[subject].map(type => (
              <button
                key={type}
                type="button"
                className={`edit-modal-chip ${(subjectTypes[subject] || []).includes(type) ? 'selected' : ''}`}
                onClick={() => toggleSubjectType(subject, type)}
                style={{ fontSize: '13px', padding: '6px 12px' }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Grade Levels Editor
const GradeLevelsEditor = ({ data, onChange, isArabic }) => {
  const [grades, setGrades] = useState(data.grade_levels || []);

  const gradeOptions = [
    ...Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`),
    'Foundation', 'Diploma', 'Bachelor', 'Master', 'PhD'
  ];

  const toggleGrade = (grade) => {
    const newGrades = grades.includes(grade)
      ? grades.filter(g => g !== grade)
      : [...grades, grade];
    setGrades(newGrades);
    onChange({ grade_levels: newGrades });
  };

  return (
    <div className="edit-modal-form">
      <div className="edit-modal-field">
        <label>{isArabic ? 'المستويات الدراسية' : 'Grade Levels'}</label>
        <div className="edit-modal-chip-grid">
          {gradeOptions.map(grade => (
            <button
              key={grade}
              type="button"
              className={`edit-modal-chip ${grades.includes(grade) ? 'selected' : ''}`}
              onClick={() => toggleGrade(grade)}
            >
              {grade}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Languages Editor
const LanguagesEditor = ({ data, onChange, isArabic }) => {
  const [languages, setLanguages] = useState(data.languages || []);

  const addLanguage = () => {
    const newLanguages = [...languages, { language: '', level: '' }];
    setLanguages(newLanguages);
    onChange({ languages: newLanguages });
  };

  const removeLanguage = (index) => {
    const newLanguages = languages.filter((_, i) => i !== index);
    setLanguages(newLanguages);
    onChange({ languages: newLanguages });
  };

  const updateLanguage = (index, field, value) => {
    const newLanguages = languages.map((lang, i) =>
      i === index ? { ...lang, [field]: value } : lang
    );
    setLanguages(newLanguages);
    onChange({ languages: newLanguages });
  };

  const languageOptions = LANGUAGES.map(l => ({ value: l, label: l }));
  const levelOptions = LANGUAGE_LEVELS.map(l => ({ value: l, label: l }));

  return (
    <div className="edit-modal-form">
      <div className="edit-modal-field">
        <label>{isArabic ? 'اللغات' : 'Languages'}</label>
        {languages.map((lang, index) => (
          <div key={index} className="edit-modal-language-row">
            <CustomDropdown
              value={lang.language}
              onChange={(val) => updateLanguage(index, 'language', val)}
              options={languageOptions}
              placeholder={isArabic ? 'اللغة' : 'Language'}
              isArabic={isArabic}
            />
            <CustomDropdown
              value={lang.level}
              onChange={(val) => updateLanguage(index, 'level', val)}
              options={levelOptions}
              placeholder={isArabic ? 'المستوى' : 'Level'}
              isArabic={isArabic}
            />
            <button type="button" className="edit-modal-remove-btn" onClick={() => removeLanguage(index)}>
              <FaTrash />
            </button>
          </div>
        ))}
        <button type="button" className="edit-modal-add-btn" onClick={addLanguage}>
          <FaPlus /> {isArabic ? 'إضافة لغة' : 'Add Language'}
        </button>
      </div>
    </div>
  );
};

// Experience Editor
const ExperienceEditor = ({ data, onChange, isArabic }) => {
  const [experiences, setExperiences] = useState(data.experiences || []);
  const [noExperience, setNoExperience] = useState(data.experience_none || false);

  const addExperience = () => {
    const newExperiences = [...experiences, {
      organization: '',
      job_title: '',
      from_month: '',
      from_year: '',
      to_month: '',
      to_year: '',
      present: false
    }];
    setExperiences(newExperiences);
    onChange({ experiences: newExperiences, experience_none: false });
  };

  const removeExperience = (index) => {
    const newExperiences = experiences.filter((_, i) => i !== index);
    setExperiences(newExperiences);
    onChange({ experiences: newExperiences });
  };

  const updateExperience = (index, field, value) => {
    const newExperiences = experiences.map((exp, i) =>
      i === index ? { ...exp, [field]: value } : exp
    );
    setExperiences(newExperiences);
    onChange({ experiences: newExperiences });
  };

  const toggleNoExperience = () => {
    const newValue = !noExperience;
    setNoExperience(newValue);
    if (newValue) {
      setExperiences([]);
    }
    onChange({ experiences: newValue ? [] : experiences, experience_none: newValue });
  };

  const monthOptions = MONTHS.map(m => ({ value: m, label: m }));
  const yearOptions = YEARS.map(y => ({ value: y, label: y }));

  return (
    <div className="edit-modal-form">
      <div className="edit-modal-field">
        <label className="edit-modal-checkbox">
          <input
            type="checkbox"
            checked={noExperience}
            onChange={toggleNoExperience}
          />
          <span>{isArabic ? 'ليس لدي خبرة' : "I don't have any experience"}</span>
        </label>
      </div>

      {!noExperience && (
        <>
          {experiences.map((exp, index) => (
            <div key={index} className="edit-modal-experience-card">
              <div className="edit-modal-experience-header">
                <span>{isArabic ? `خبرة ${index + 1}` : `Experience ${index + 1}`}</span>
                <button type="button" className="edit-modal-remove-btn" onClick={() => removeExperience(index)}>
                  <FaTrash />
                </button>
              </div>
              <div className="edit-modal-field">
                <label>{isArabic ? 'المؤسسة' : 'Organization'}</label>
                <input
                  type="text"
                  value={exp.organization}
                  onChange={(e) => updateExperience(index, 'organization', e.target.value)}
                  placeholder={isArabic ? 'اسم المؤسسة' : 'Organization name'}
                />
              </div>
              <div className="edit-modal-field">
                <label>{isArabic ? 'المسمى الوظيفي' : 'Job Title'}</label>
                <input
                  type="text"
                  value={exp.job_title}
                  onChange={(e) => updateExperience(index, 'job_title', e.target.value)}
                  placeholder={isArabic ? 'المسمى الوظيفي' : 'Your job title'}
                />
              </div>
              <div className="edit-modal-date-range">
                <div className="edit-modal-field">
                  <label>{isArabic ? 'من' : 'From'}</label>
                  <div className="edit-modal-date-row">
                    <CustomDropdown
                      value={exp.from_month}
                      onChange={(val) => updateExperience(index, 'from_month', val)}
                      options={monthOptions}
                      placeholder={isArabic ? 'الشهر' : 'Month'}
                      isArabic={isArabic}
                    />
                    <CustomDropdown
                      value={exp.from_year}
                      onChange={(val) => updateExperience(index, 'from_year', val)}
                      options={yearOptions}
                      placeholder={isArabic ? 'السنة' : 'Year'}
                      isArabic={isArabic}
                    />
                  </div>
                </div>
                <div className="edit-modal-field">
                  <label>{isArabic ? 'إلى' : 'To'}</label>
                  <label className="edit-modal-checkbox" style={{ marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={exp.present}
                      onChange={(e) => updateExperience(index, 'present', e.target.checked)}
                    />
                    <span>{isArabic ? 'حتى الآن' : 'Present'}</span>
                  </label>
                  {!exp.present && (
                    <div className="edit-modal-date-row">
                      <CustomDropdown
                        value={exp.to_month}
                        onChange={(val) => updateExperience(index, 'to_month', val)}
                        options={monthOptions}
                        placeholder={isArabic ? 'الشهر' : 'Month'}
                        isArabic={isArabic}
                      />
                      <CustomDropdown
                        value={exp.to_year}
                        onChange={(val) => updateExperience(index, 'to_year', val)}
                        options={yearOptions}
                        placeholder={isArabic ? 'السنة' : 'Year'}
                        isArabic={isArabic}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <button type="button" className="edit-modal-add-btn" onClick={addExperience}>
            <FaPlus /> {isArabic ? 'إضافة خبرة' : 'Add Experience'}
          </button>
        </>
      )}
    </div>
  );
};

// Pricing Editor
const PricingEditor = ({ data, onChange, isArabic }) => {
  const [pricing, setPricing] = useState({
    pricing_private_per_hour: data.pricing_private_per_hour || '',
    pricing_group_per_hour: data.pricing_group_per_hour || '',
    group_pricing_model: data.group_pricing_model || 'per_person'
  });

  const handleChange = (field, value) => {
    const newPricing = { ...pricing, [field]: value };
    setPricing(newPricing);
    onChange(newPricing);
  };

  return (
    <div className="edit-modal-form">
      <div className="edit-modal-field">
        <label>{isArabic ? 'السعر بالساعة (خاص)' : 'Per hour (private)'}</label>
        <CustomDropdown
          value={pricing.pricing_private_per_hour}
          onChange={(val) => handleChange('pricing_private_per_hour', val)}
          options={PRICE_OPTIONS}
          placeholder={isArabic ? 'اختر السعر' : 'Select price'}
          isArabic={isArabic}
        />
      </div>
      <div className="edit-modal-field">
        <label>{isArabic ? 'السعر بالساعة (مجموعة)' : 'Per hour (group)'}</label>
        <CustomDropdown
          value={pricing.pricing_group_per_hour}
          onChange={(val) => handleChange('pricing_group_per_hour', val)}
          options={PRICE_OPTIONS}
          placeholder={isArabic ? 'اختر السعر' : 'Select price'}
          isArabic={isArabic}
        />
      </div>
      <div className="edit-modal-field">
        <label>{isArabic ? 'نموذج تسعير المجموعة' : 'Group Pricing Model'}</label>
        <div className="edit-modal-radio-group">
          <label className={`edit-modal-radio ${pricing.group_pricing_model === 'per_person' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="group_pricing_model"
              value="per_person"
              checked={pricing.group_pricing_model === 'per_person'}
              onChange={() => handleChange('group_pricing_model', 'per_person')}
            />
            <span>{isArabic ? 'لكل طالب' : 'Per Student'}</span>
          </label>
          <label className={`edit-modal-radio ${pricing.group_pricing_model === 'fixed' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="group_pricing_model"
              value="fixed"
              checked={pricing.group_pricing_model === 'fixed'}
              onChange={() => handleChange('group_pricing_model', 'fixed')}
            />
            <span>{isArabic ? 'سعر ثابت' : 'Fixed Price'}</span>
          </label>
        </div>
      </div>
    </div>
  );
};

// Banking Editor
const BankingEditor = ({ data, onChange, isArabic }) => {
  const [banking, setBanking] = useState({
    payment_method: data.payment_method || '',
    iban: data.iban || '',
    bank_name: data.bank_name || '',
    account_holder_name: data.account_holder_name || ''
  });

  const handleChange = (field, value) => {
    const newBanking = { ...banking, [field]: value };
    setBanking(newBanking);
    onChange(newBanking);
  };

  const bankOptions = BAHRAIN_BANKS.map(b => ({
    value: b.value,
    label: isArabic ? b.labelAr : b.labelEn
  }));

  return (
    <div className="edit-modal-form">
      {/* Payment Method Selection */}
      <div className="edit-modal-field">
        <label>{isArabic ? 'طريقة استلام الدفع' : 'How would you like to receive payments?'}</label>
        <div className="edit-modal-radio-group">
          <label className={`edit-modal-radio ${banking.payment_method === 'benefitpay' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="payment_method"
              value="benefitpay"
              checked={banking.payment_method === 'benefitpay'}
              onChange={() => handleChange('payment_method', 'benefitpay')}
            />
            <span>{isArabic ? 'بنفت باي' : 'BenefitPay'}</span>
          </label>
          <label className={`edit-modal-radio ${banking.payment_method === 'bank_transfer' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="payment_method"
              value="bank_transfer"
              checked={banking.payment_method === 'bank_transfer'}
              onChange={() => handleChange('payment_method', 'bank_transfer')}
            />
            <span>{isArabic ? 'تحويل بنكي' : 'Bank Transfer'}</span>
          </label>
        </div>
      </div>

      {/* IBAN - Always shown */}
      <div className="edit-modal-field">
        <label>IBAN</label>
        <input
          type="text"
          value={banking.iban}
          onChange={(e) => handleChange('iban', e.target.value.toUpperCase())}
          placeholder="BH67BMAG00001299376601"
        />
      </div>

      {/* Bank Name - Always shown */}
      <div className="edit-modal-field">
        <label>{isArabic ? 'اسم البنك' : 'Bank Name'}</label>
        <CustomDropdown
          value={banking.bank_name}
          onChange={(val) => handleChange('bank_name', val)}
          options={bankOptions}
          placeholder={isArabic ? 'اختر البنك' : 'Select bank'}
          isArabic={isArabic}
        />
      </div>

      {/* Account Holder Name - Always shown */}
      <div className="edit-modal-field">
        <label>{isArabic ? 'اسم صاحب الحساب' : 'Account Holder Name'}</label>
        <input
          type="text"
          value={banking.account_holder_name}
          onChange={(e) => handleChange('account_holder_name', e.target.value)}
          placeholder={isArabic ? 'الاسم كما يظهر في البنك' : 'Name as it appears on bank account'}
        />
      </div>
    </div>
  );
};

// Availability Editor
const AvailabilityEditor = ({ data, onChange, isArabic, language }) => {
  const [schedule, setSchedule] = useState(data.availability_schedule || {});

  const updateDay = (day, field, value) => {
    const dayData = schedule[day] || { notAvailable: true, allDay: false, slots: [] };
    const newDayData = { ...dayData, [field]: value };

    if (field === 'notAvailable' && value) {
      newDayData.allDay = false;
      newDayData.slots = [];
    }
    if (field === 'allDay' && value) {
      newDayData.notAvailable = false;
      newDayData.slots = [];
    }

    const newSchedule = { ...schedule, [day]: newDayData };
    setSchedule(newSchedule);
    onChange({ availability_schedule: newSchedule });
  };

  const addSlot = (day) => {
    const dayData = schedule[day] || { notAvailable: false, allDay: false, slots: [] };
    const newSlots = [...(dayData.slots || []), { from: '09:00', to: '17:00' }];
    const newDayData = { ...dayData, slots: newSlots, notAvailable: false, allDay: false };
    const newSchedule = { ...schedule, [day]: newDayData };
    setSchedule(newSchedule);
    onChange({ availability_schedule: newSchedule });
  };

  const removeSlot = (day, slotIndex) => {
    const dayData = schedule[day] || { notAvailable: false, allDay: false, slots: [] };
    const newSlots = dayData.slots.filter((_, i) => i !== slotIndex);
    const newDayData = { ...dayData, slots: newSlots };
    const newSchedule = { ...schedule, [day]: newDayData };
    setSchedule(newSchedule);
    onChange({ availability_schedule: newSchedule });
  };

  const updateSlot = (day, slotIndex, field, value) => {
    const dayData = schedule[day] || { notAvailable: false, allDay: false, slots: [] };
    const newSlots = dayData.slots.map((slot, i) =>
      i === slotIndex ? { ...slot, [field]: value } : slot
    );
    const newDayData = { ...dayData, slots: newSlots };
    const newSchedule = { ...schedule, [day]: newDayData };
    setSchedule(newSchedule);
    onChange({ availability_schedule: newSchedule });
  };

  const timeOptions = TIME_SLOTS.map(t => ({ value: t.time24, label: t.time12 }));

  return (
    <div className="edit-modal-form">
      {DAYS.map(day => {
        const dayData = schedule[day] || { notAvailable: true, allDay: false, slots: [] };
        const dayLabel = DAY_LABELS[language]?.[day] || DAY_LABELS['EN'][day];

        return (
          <div key={day} className="edit-modal-day-card">
            <div className="edit-modal-day-header">
              <span className="edit-modal-day-name">{dayLabel}</span>
              <div className="edit-modal-day-options">
                <label className="edit-modal-checkbox-small">
                  <input
                    type="checkbox"
                    checked={dayData.notAvailable}
                    onChange={(e) => updateDay(day, 'notAvailable', e.target.checked)}
                  />
                  <span>{isArabic ? 'غير متاح' : 'Not Available'}</span>
                </label>
                <label className="edit-modal-checkbox-small">
                  <input
                    type="checkbox"
                    checked={dayData.allDay}
                    onChange={(e) => updateDay(day, 'allDay', e.target.checked)}
                  />
                  <span>{isArabic ? 'طوال اليوم' : 'All Day'}</span>
                </label>
              </div>
            </div>

            {!dayData.notAvailable && !dayData.allDay && (
              <div className="edit-modal-slots">
                {(dayData.slots || []).map((slot, slotIndex) => (
                  <div key={slotIndex} className="edit-modal-slot-row">
                    <CustomDropdown
                      value={slot.from}
                      onChange={(val) => updateSlot(day, slotIndex, 'from', val)}
                      options={timeOptions}
                      placeholder={isArabic ? 'من' : 'From'}
                      isArabic={isArabic}
                    />
                    <span className="edit-modal-slot-separator">-</span>
                    <CustomDropdown
                      value={slot.to}
                      onChange={(val) => updateSlot(day, slotIndex, 'to', val)}
                      options={timeOptions}
                      placeholder={isArabic ? 'إلى' : 'To'}
                      isArabic={isArabic}
                    />
                    <button type="button" className="edit-modal-remove-btn-small" onClick={() => removeSlot(day, slotIndex)}>
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <button type="button" className="edit-modal-add-slot-btn" onClick={() => addSlot(day)}>
                  <FaPlus /> {isArabic ? 'إضافة فترة' : 'Add Slot'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Teaching Locations Editor
const TeachingLocationsEditor = ({ data, onChange, isArabic }) => {
  const [lessonFormat, setLessonFormat] = useState(data.lesson_format_preference || 'online_only');

  const handleFormatChange = (format) => {
    setLessonFormat(format);
    onChange({ lesson_format_preference: format });
  };

  return (
    <div className="edit-modal-form">
      <div className="edit-modal-field">
        <label>{isArabic ? 'كيف تفضل تقديم الدروس؟' : 'How do you prefer to deliver lessons?'}</label>
        <div className="edit-modal-format-options">
          <label className={`edit-modal-format-card ${lessonFormat === 'online_only' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="lesson_format"
              value="online_only"
              checked={lessonFormat === 'online_only'}
              onChange={() => handleFormatChange('online_only')}
            />
            <span className="edit-modal-format-label">{isArabic ? 'أونلاين فقط' : 'Online Only'}</span>
          </label>
          <label className={`edit-modal-format-card ${lessonFormat === 'face_to_face_only' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="lesson_format"
              value="face_to_face_only"
              checked={lessonFormat === 'face_to_face_only'}
              onChange={() => handleFormatChange('face_to_face_only')}
            />
            <span className="edit-modal-format-label">{isArabic ? 'وجهاً لوجه فقط' : 'Face-to-Face Only'}</span>
          </label>
          <label className={`edit-modal-format-card ${lessonFormat === 'both' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="lesson_format"
              value="both"
              checked={lessonFormat === 'both'}
              onChange={() => handleFormatChange('both')}
            />
            <span className="edit-modal-format-label">{isArabic ? 'كلاهما' : 'Both'}</span>
          </label>
        </div>
      </div>
    </div>
  );
};

// Video Editor
const VideoEditor = ({ data, onChange, isArabic }) => {
  const videoUrl = data.intro_video_url || '';

  const handleRemoveVideo = () => {
    onChange({ intro_video_url: '' });
  };

  return (
    <div className="edit-modal-form">
      {videoUrl ? (
        <>
          <div className="edit-modal-field">
            <label>{isArabic ? 'فيديو التعريف الخاص بك' : 'Your Introduction Video'}</label>
            <div style={{
              width: '100%',
              aspectRatio: '16/9',
              borderRadius: '10px',
              overflow: 'hidden',
              background: '#f3f4f6'
            }}>
              <video controls src={videoUrl} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
          </div>

          <button
            type="button"
            onClick={handleRemoveVideo}
            style={{
              padding: '10px 16px',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              background: '#fef2f2',
              color: '#ef4444',
              fontSize: '14px',
              cursor: 'pointer',
              width: 'fit-content'
            }}
          >
            {isArabic ? 'إزالة الفيديو' : 'Remove Video'}
          </button>
        </>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          background: '#f9fafb',
          borderRadius: '10px',
          color: '#6b7280'
        }}>
          <p style={{ margin: 0, fontSize: '14px' }}>
            {isArabic
              ? 'لا يوجد فيديو مرفوع. يمكنك رفع فيديو من صفحة التسجيل.'
              : 'No video uploaded. You can upload a video from the registration wizard.'}
          </p>
        </div>
      )}
    </div>
  );
};

// Main Profile Edit Modal Component
export default function ProfileEditModal({
  open,
  onClose,
  section,
  wizardData,
  onSave,
  isArabic,
  language,
  isSaving
}) {
  const [editedData, setEditedData] = useState({});
  const modalRef = useRef(null);

  useEffect(() => {
    if (open) {
      setEditedData({});
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, section]);

  const handleChange = (data) => {
    setEditedData(prev => ({ ...prev, ...data }));
  };

  const handleSave = () => {
    onSave(editedData);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  const sectionTitles = {
    information: isArabic ? 'المعلومات' : 'Information',
    aboutMe: isArabic ? 'نبذة عني' : 'About Me',
    subjects: isArabic ? 'المواد' : 'Subjects',
    gradeLevels: isArabic ? 'المستويات' : 'Grade Levels',
    languages: isArabic ? 'اللغات' : 'Languages',
    experience: isArabic ? 'الخبرة' : 'Experience',
    pricing: isArabic ? 'التسعير' : 'Pricing',
    banking: isArabic ? 'معلومات البنك' : 'Banking Information',
    availability: isArabic ? 'التوفر' : 'Availability',
    teachingLocations: isArabic ? 'مواقع التدريس' : 'Teaching Locations',
    video: isArabic ? 'الفيديو التعريفي' : 'Intro Video'
  };

  const renderEditor = () => {
    switch (section) {
      case 'information':
        return <InformationEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'aboutMe':
        return <AboutMeEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'subjects':
        return <SubjectsEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'gradeLevels':
        return <GradeLevelsEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'languages':
        return <LanguagesEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'experience':
        return <ExperienceEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'pricing':
        return <PricingEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'banking':
        return <BankingEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'availability':
        return <AvailabilityEditor data={wizardData} onChange={handleChange} isArabic={isArabic} language={language} />;
      case 'teachingLocations':
        return <TeachingLocationsEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      case 'video':
        return <VideoEditor data={wizardData} onChange={handleChange} isArabic={isArabic} />;
      default:
        return <div>{isArabic ? 'قسم غير معروف' : 'Unknown section'}</div>;
    }
  };

  return (
    <div className="edit-modal-overlay" onClick={handleBackdropClick} dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="edit-modal-container" ref={modalRef}>
        <div className="edit-modal-header">
          <h2 className="edit-modal-title">{sectionTitles[section] || section}</h2>
          <button type="button" className="edit-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="edit-modal-content">
          {renderEditor()}
        </div>
        <div className="edit-modal-footer">
          <button type="button" className="edit-modal-btn edit-modal-btn-cancel" onClick={onClose} disabled={isSaving}>
            {isArabic ? 'إلغاء' : 'Cancel'}
          </button>
          <button type="button" className="edit-modal-btn edit-modal-btn-save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (isArabic ? 'حفظ' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}
